import React, { useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { NavLink } from "react-router-dom";
import "./RacquetGameIndividuals.css";

function RacquetGameIndividuals({ game, theme, slug }) {
  const racquetMatches = useMemo(
    () => game?.racquetMatches || [],
    [game?.racquetMatches]
  );

  if (!game) {
    return (
      <div className={`rgi-page rgi-theme-${theme || "light"}`}>
        <div className="rgi-empty-state">
          Game information is unavailable.
        </div>
      </div>
    );
  }

  const match = racquetMatches[0] || null;

  const isDoublesPair = Boolean(
    game.match_format === "doubles" ||
    game.home_partner_player_id ||
    game.away_partner_player_id ||
    game.home_doubles_pair_id ||
    game.away_doubles_pair_id ||
    match?.home_player_2_id ||
    match?.away_player_2_id
  );

  const matchTypeLabel = isDoublesPair
    ? "Doubles Match"
    : "Singles Match";

  const formattedDate = game.date
    ? formatInTimeZone(
      new Date(`${game.date}T12:00:00`),
      "America/Los_Angeles",
      "MMM d, yyyy"
    )
    : "Date TBA";

  const formattedTime =
    game.date && game.time
      ? formatInTimeZone(
        new Date(`${game.date}T${game.time}`),
        "America/Los_Angeles",
        "h:mm a"
      )
      : "Time TBA";

  const getPlayerPath = (playerId) => {
    if (!playerId) return "#";

    return slug
      ? `/${slug}/players/${playerId}`
      : `/players/${playerId}`;
  };

  const getPlayerName = (player) => {
    if (!player) return "TBD";

    return (
      player.name ||
      player.full_name ||
      `${player.firstName || ""} ${player.lastName || ""}`.trim() ||
      `${player.first_name || ""} ${player.last_name || ""}`.trim() ||
      player.player?.name ||
      player.player?.full_name ||
      `${player.player?.firstName || ""} ${player.player?.lastName || ""
        }`.trim() ||
      `${player.player?.first_name || ""} ${player.player?.last_name || ""
        }`.trim() ||
      "TBD"
    );
  };

  const getPlayerId = (player) => {
    if (!player) return null;

    return (
      player.player_id ||
      player.id ||
      player.player?.id ||
      null
    );
  };

  const getMatchPlayer = (side, position) => {
    if (match) {
      const camelPlayer = match?.[`${side}Player${position}`];

      if (camelPlayer) {
        return camelPlayer;
      }

      const snakePlayer = match?.[`${side}_player_${position}`];

      if (snakePlayer) {
        return snakePlayer;
      }
    }

    if (position === 1) {
      const gameEntry =
        side === "home"
          ? game.home_player
          : game.away_player;

      if (gameEntry) {
        return gameEntry.player || gameEntry;
      }
    }

    if (position === 2) {
      const partnerEntry =
        side === "home"
          ? game.home_partner_player
          : game.away_partner_player;

      if (partnerEntry) {
        return partnerEntry.player || partnerEntry;
      }

      const camelPartner =
        side === "home"
          ? game.homePartnerPlayer
          : game.awayPartnerPlayer;

      if (camelPartner) {
        return camelPartner.player || camelPartner;
      }
    }

    return null;
  };

  const homePlayers = [
    getMatchPlayer("home", 1),
    getMatchPlayer("home", 2),
  ].filter(Boolean);

  const awayPlayers = [
    getMatchPlayer("away", 1),
    getMatchPlayer("away", 2),
  ].filter(Boolean);

  const renderPlayerName = (player) => {
    const playerId = getPlayerId(player);

    if (playerId) {
      return (
        <NavLink
          to={getPlayerPath(playerId)}
          className="rgi-player-name-link"
        >
          {getPlayerName(player)}
        </NavLink>
      );
    }

    return (
      <span className="rgi-player-name">
        {getPlayerName(player)}
      </span>
    );
  };

  const renderSideNames = (players) => {
    if (!players.length) {
      return <span>TBD</span>;
    }

    return players.map((player, index) => {
      const playerId = getPlayerId(player);

      return (
        <React.Fragment
          key={playerId || `player-${index}`}
        >
          {index > 0 && (
            <span className="rgi-player-separator">
              /
            </span>
          )}

          {renderPlayerName(player)}
        </React.Fragment>
      );
    });
  };

  const homeSideLabel =
    homePlayers
      .map((player) => getPlayerName(player))
      .join(" / ") || "Home";

  const awaySideLabel =
    awayPlayers
      .map((player) => getPlayerName(player))
      .join(" / ") || "Away";

  const hasWinner = Boolean(match?.winner_side);

  const gameStatus =
    game.status === "completed" || hasWinner
      ? "FINAL"
      : "SCHEDULED";

  const winnerSide = match?.winner_side || null;

  const scores = useMemo(() => {
    return [...(match?.scores || [])].sort(
      (a, b) =>
        Number(a.set_number || 0) -
        Number(b.set_number || 0)
    );
  }, [match?.scores]);

  const formatSetScore = (score, side) => {
    const value =
      side === "home"
        ? score.home_score
        : score.away_score;

    return value ?? "—";
  };

  const isSetWinner = (score, side) => {
    const homeScore = Number(score.home_score ?? 0);
    const awayScore = Number(score.away_score ?? 0);

    if (side === "home") {
      return homeScore > awayScore;
    }

    return awayScore > homeScore;
  };

  const statDefinitionsById = {};

  (match?.stats || []).forEach((entry) => {
    const statDefinition = entry?.stat;

    if (!entry?.stat_id || !statDefinition) {
      return;
    }

    if (statDefinition.is_visible === false) {
      return;
    }

    statDefinitionsById[entry.stat_id] = {
      id: entry.stat_id,
      name: statDefinition.name || "Stat",
      abbreviation:
        statDefinition.abbreviation || "",
      displayOrder:
        statDefinition.display_order ?? 999,
    };
  });

  const visibleStats = Object.values(
    statDefinitionsById
  ).sort(
    (a, b) =>
      a.displayOrder - b.displayOrder
  );

  const playerStatValues = {};

  (match?.stats || []).forEach((entry) => {
    if (!entry?.stat_id || !entry?.player_id) return;

    if (entry?.stat?.is_visible === false) {
      return;
    }

    const playerId = Number(entry.player_id);
    const statId = Number(entry.stat_id);
    const value = Number(entry.value || 0);

    if (!playerStatValues[playerId]) {
      playerStatValues[playerId] = {};
    }

    playerStatValues[playerId][statId] =
      (playerStatValues[playerId][statId] || 0) + value;
  });

  const normalizeStatName = (name = "") =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const findStatId = (...names) => {
    const normalizedNames = names.map(normalizeStatName);

    const found = visibleStats.find((stat) =>
      normalizedNames.includes(normalizeStatName(stat.name))
    );

    return found?.id || null;
  };

  const acesStatId = findStatId(
    "Aces",
    "Ace"
  );
  const firstServeAttemptsStatId = findStatId(
    "First Serve Attempts",
    "1st Serve Attempts"
  );

  const firstServeInStatId = findStatId(
    "First Serve In",
    "First Serves In",
    "1st Serve In"
  );

  const secondServeAttemptsStatId = findStatId(
    "Second Serve Attempts",
    "2nd Serve Attempts"
  );

  const secondServeInStatId = findStatId(
    "Second Serve In",
    "Second Serves In",
    "2nd Serve In"
  );

  const shotsMadeStatId = findStatId(
    "Shots Made",
    "Shot Made"
  );

  const shotsMissedStatId = findStatId(
    "Shots Missed",
    "Shot Missed"
  );
  const forehandWinnersStatId = findStatId(
    "Forehand Winners",
    "Forehand Winner"
  );

  const backhandWinnersStatId = findStatId(
    "Backhand Winners",
    "Backhand Winner"
  );
  const getPlayerStatValue = (playerId, statId) => {
    if (!playerId || !statId) return 0;

    return Number(
      playerStatValues[Number(playerId)]?.[Number(statId)] || 0
    );
  };

  const calculatePercentage = (made, attempts) => {
    const madeNumber = Number(made || 0);
    const attemptNumber = Number(attempts || 0);

    if (attemptNumber <= 0) return "—";

    return `${Math.round((madeNumber / attemptNumber) * 100)}%`;
  };

  const getPlayerCalculatedStats = (player) => {
    const playerId = getPlayerId(player);
    const aces = getPlayerStatValue(
      playerId,
      acesStatId
    );
    const firstServeAttempts = getPlayerStatValue(
      playerId,
      firstServeAttemptsStatId
    );

    const firstServeIn = getPlayerStatValue(
      playerId,
      firstServeInStatId
    );

    const secondServeAttempts = getPlayerStatValue(
      playerId,
      secondServeAttemptsStatId
    );

    const secondServeIn = getPlayerStatValue(
      playerId,
      secondServeInStatId
    );

    const shotsMade = getPlayerStatValue(
      playerId,
      shotsMadeStatId
    );

    const shotsMissed = getPlayerStatValue(
      playerId,
      shotsMissedStatId
    );
    const forehandWinners = getPlayerStatValue(
      playerId,
      forehandWinnersStatId
    );

    const backhandWinners = getPlayerStatValue(
      playerId,
      backhandWinnersStatId
    );

    const totalWinners =
      forehandWinners + backhandWinners;
    return {
      aces,

      firstServePercentage: calculatePercentage(
        firstServeIn,
        firstServeAttempts
      ),

      secondServePercentage: calculatePercentage(
        secondServeIn,
        secondServeAttempts
      ),

      shotConsistency: calculatePercentage(
        shotsMade,
        shotsMade + shotsMissed
      ),

      forehandWinners,
      backhandWinners,
      totalWinners,
    };
  };
  
  const allPlayers = [
    ...homePlayers,
    ...awayPlayers,
  ].filter(
    (player, index, array) =>
      array.findIndex(
        (item) =>
          Number(getPlayerId(item)) ===
          Number(getPlayerId(player))
      ) === index
  );
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url);

      if (parsedUrl.hostname.includes("youtube.com")) {
        const videoId = parsedUrl.searchParams.get("v");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      if (parsedUrl.hostname === "youtu.be") {
        const videoId = parsedUrl.pathname.replace("/", "");

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      return url;
    } catch {
      return url;
    }
  };

  const videoEmbedUrl = getVideoEmbedUrl(game.video_url);
  return (
    <div
      className={`rgi-page rgi-theme-${theme || "light"
        }`}
    >
      <main className="rgi-shell">
        <section className="rgi-match-card">
          <div className="rgi-match-type">
            <span>{matchTypeLabel}</span>
          </div>

          <div className="rgi-meta-row">
            <span>
              {game.location || "Location TBA"}
            </span>

            <span className="rgi-meta-dot">
              •
            </span>

            <span>{formattedDate}</span>

            <span className="rgi-meta-dot">
              •
            </span>

            <span>{formattedTime}</span>
          </div>

          <div className="rgi-status-row">
            <span
              className={`rgi-status-pill ${gameStatus === "FINAL"
                  ? "rgi-status-final"
                  : "rgi-status-scheduled"
                }`}
            >
              {gameStatus}
            </span>
          </div>

          {scores.length === 0 ? (
            <div className="rgi-upcoming-matchup">
              <div className="rgi-upcoming-side">
                <div className="rgi-upcoming-label">
                  {isDoublesPair ? "Pair 1" : "Player 1"}
                </div>

                <div className="rgi-upcoming-players">
                  {homePlayers.length > 0 ? (
                    homePlayers.map((player, index) => (
                      <div
                        className="rgi-upcoming-player"
                        key={
                          getPlayerId(player) ||
                          `home-${index}`
                        }
                      >
                        {renderPlayerName(player)}
                      </div>
                    ))
                  ) : (
                    <div className="rgi-upcoming-player">
                      TBD
                    </div>
                  )}
                </div>
              </div>

              <div className="rgi-versus">
                VS
              </div>

              <div className="rgi-upcoming-side rgi-upcoming-side-away">
                <div className="rgi-upcoming-label">
                  {isDoublesPair ? "Pair 2" : "Player 2"}
                </div>

                <div className="rgi-upcoming-players">
                  {awayPlayers.length > 0 ? (
                    awayPlayers.map((player, index) => (
                      <div
                        className="rgi-upcoming-player"
                        key={
                          getPlayerId(player) ||
                          `away-${index}`
                        }
                      >
                        {renderPlayerName(player)}
                      </div>
                    ))
                  ) : (
                    <div className="rgi-upcoming-player">
                      TBD
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="rgi-score-header">
                <div>
                  {isDoublesPair ? "Pair" : "Player"}
                </div>

                <div className="rgi-set-columns">
                  {scores.map((score, index) => (
                    <span key={score.id || index}>
                      {Number(score.set_number) > 0
                        ? `Set ${score.set_number}`
                        : `Set ${index + 1}`}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className={`rgi-score-row ${winnerSide === "home"
                    ? "rgi-score-row-winner"
                    : ""
                  }`}
              >
                <div className="rgi-side-name">
                  <span className="rgi-winner-slot">
                    {winnerSide === "home" && (
                      <span className="rgi-winner-check">
                        ✓
                      </span>
                    )}
                  </span>

                  <div className="rgi-side-player-list">
                    {renderSideNames(homePlayers)}
                  </div>
                </div>

                <div className="rgi-set-columns rgi-set-values">
                  {scores.map((score, index) => (
                    <span
                      key={score.id || index}
                      className={
                        isSetWinner(score, "home")
                          ? "rgi-set-winner"
                          : ""
                      }
                    >
                      {formatSetScore(score, "home")}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className={`rgi-score-row ${winnerSide === "away"
                    ? "rgi-score-row-winner"
                    : ""
                  }`}
              >
                <div className="rgi-side-name">
                  <span className="rgi-winner-slot">
                    {winnerSide === "away" && (
                      <span className="rgi-winner-check">
                        ✓
                      </span>
                    )}
                  </span>

                  <div className="rgi-side-player-list">
                    {renderSideNames(awayPlayers)}
                  </div>
                </div>

                <div className="rgi-set-columns rgi-set-values">
                  {scores.map((score, index) => (
                    <span
                      key={score.id || index}
                      className={
                        isSetWinner(score, "away")
                          ? "rgi-set-winner"
                          : ""
                      }
                    >
                      {formatSetScore(score, "away")}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
        {videoEmbedUrl && (
          <section className="rgi-video-card">
            <div className="rgi-section-heading">
              <h2>Match Video</h2>
            </div>

            <div className="rgi-video-wrap">
              <iframe
                src={videoEmbedUrl}
                title="Match Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        )}
        {allPlayers.length > 0 && (
          <section className="rgi-stats-card">
            <div className="rgi-section-heading">
              <div className="rgi-heading-icon">
                ▥
              </div>

              <h2>Player Stats</h2>
            </div>

            <div className="rgi-stats-table-wrap">
              <table className="rgi-stats-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>First Serve %</th>
                    <th>Second Serve %</th>
                    <th>Shot Consistency</th>
                    <th>Forehand Winners</th>
                    <th>Backhand Winners</th>
                    <th>Total Winners</th>
                    <th>Aces</th>
                  </tr>
                </thead>

                <tbody>
                  {allPlayers.map((player, index) => {
                    const playerId = getPlayerId(player);

                    const calculatedStats =
                      getPlayerCalculatedStats(player);

                    return (
                      <tr
                        key={
                          playerId ||
                          `stats-player-${index}`
                        }
                      >
                        <td>
                          {renderPlayerName(player)}
                        </td>
                        <td>
                          {
                            calculatedStats.firstServePercentage
                          }
                        </td>

                        <td>
                          {
                            calculatedStats.secondServePercentage
                          }
                        </td>

                        <td>
                          {
                            calculatedStats.shotConsistency
                          }
                        </td>
                        <td>
                          {calculatedStats.forehandWinners}
                        </td>

                        <td>
                          {calculatedStats.backhandWinners}
                        </td>

                        <td>
                          {calculatedStats.totalWinners}
                        </td>
                        <td>
                          {calculatedStats.aces}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default RacquetGameIndividuals;